import { useMemo } from "react"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import LicenseContentDetail from "@/v2-backoffice-client/license/changes/LicenseContentDetail"
import LicenseContentViewInfo from "@/v2-backoffice-client/license/changes/LicenseContentViewInfo"
import LicenseContentViewToolbar from "@/v2-backoffice-client/license/changes/LicenseContentViewToolbar"
import LicenseDecisionForm from "@/v2-backoffice-client/license/changes/LicenseDecisionForm"
import { LicenseChangesContext } from "@/v2-backoffice-client/license/changes/license.changes.context"
import { useContentDecisionWriter } from "@/v2-backoffice-client/license/changes/license.changes.decide.hooks"
import { contentDecisionSubjectOf } from "@/v2-backoffice-client/license/changes/license.content.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"

// The map-content panel (?v=mapContent:<code>): a layer or base map as the
// Feature access board sees it. Fed by the same content catalog the board
// renders, so board→panel opens from the Apollo cache, and keyed by the
// feature code because that is what the decision is written against and
// what a layer's or base map's sheet links by. Access first, the one
// decision this board exists for; Details for what the item is, which
// packages carry it and where it is made.
function LicenseContentViewSheet() {
  const { id: code } = useAppViewSheetOpener()

  const { data, previousData, loading, error } = useFeatureContentCatalogQuery({
    skip: !code,
  })
  const { data: packagesData } = useFeatureGroupsQuery({ skip: !code })
  // previousData keeps the panel rendered through post-save evictions,
  // when the cache is briefly empty and data undefined.
  const items = (data ?? previousData)?.getFeatureContentCatalog ?? []
  const item = code
    ? items.find((candidate) => candidate.Code === code)
    : undefined
  const packages = useMemo(
    () => packagesData?.getFeatureGroups ?? [],
    [packagesData?.getFeatureGroups],
  )

  const { write, isSaving } = useContentDecisionWriter()

  return (
    <LicenseChangesContext value={{ packages }}>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading && !item}>
          {item?.Name}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <LicenseContentViewInfo item={item} isLoading={loading && !item} />
        <LicenseContentViewToolbar item={item} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="license-content-view-tabs"
        defaultValue="access"
        // Only gate the tabs while there is nothing to show: the catalog
        // reports loading on every revalidation (a save evicts it), and
        // gating on that would flash the panel.
        isLoading={loading && !item}
        error={error}
        isEmpty={!loading && !item}
        emptyLabel="Not in the content catalog. A layer or base map appears here once it has a catalog row."
      >
        <Tabs.List>
          <Tabs.Trigger value="access" iconSrc="key">
            Access
          </Tabs.Trigger>
          <Tabs.Trigger value="details" iconSrc="info">
            Details
          </Tabs.Trigger>
        </Tabs.List>

        {/* flex-1 and no overflow-auto: the form scrolls its own middle and
            keeps the Save in reach at the foot of the tab. */}
        <Tabs.Content value="access" className="min-h-0 flex-1 p-1">
          {item && (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              {!item.IsEnabled && (
                <FeatureNotice tone="info">
                  Not live yet: nobody sees this item until the review queue
                  publishes it, whatever is decided here. The decision waits for
                  it.
                </FeatureNotice>
              )}
              <LicenseDecisionForm
                // A save or a switch of row starts the draft afresh from
                // what is saved.
                key={`${item.Code}:${item.FeatureAvailabilityTypeCode}:${item.FeatureGroupIds ?? ""}`}
                subject={contentDecisionSubjectOf(item)}
                packages={packages}
                isSaving={isSaving}
                onDecide={(decision, packageIds) =>
                  write(item.Code, decision, packageIds)
                }
              />
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="details" className="min-h-0 overflow-auto p-1">
          {item && <LicenseContentDetail item={item} />}
        </Tabs.Content>
      </Tabs>
    </LicenseChangesContext>
  )
}

export default LicenseContentViewSheet
